import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Plus, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import type { ChampionshipListItem, ChampionshipStatus, PaginatedResult, Provider } from "@/types";
import { CHAMPIONSHIP_STATUS_LABEL, championshipTone, personName } from "@/lib/championships";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, Badge } from "@/components/ui/Card";
import { TableSkeleton } from "@/components/ui/Skeleton";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Pagination } from "@/components/ui/Pagination";
import { ExportExcelButton } from "@/components/ui/ExportExcelButton";
import { SortHeader, nextSort, type SortState } from "@/components/ui/SortHeader";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

type SortKey = "nombre" | "organizador" | "estado" | "equiposCount" | "partidosCount";

const selectClass =
  "w-full rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20";

export function ChampionshipsPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const isSuperAdmin = role === "SUPER_ADMIN";
  const canCreate = role === "SUPER_ADMIN" || role === "PROVIDER";

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [nombreInput, setNombreInput] = useState("");
  const nombre = useDebouncedValue(nombreInput.trim());
  const [estado, setEstado] = useState<"" | ChampionshipStatus>("");
  const [providerId, setProviderId] = useState("");
  const [sort, setSort] = useState<SortState<SortKey>>({ key: "nombre", dir: "asc" });

  const { data: providers } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => (await api.get<{ data: Provider[] }>("/providers?limit=100")).data.data,
    enabled: isSuperAdmin,
  });

  // Mismos filtros y orden para el listado y para el Excel (el Excel va sin paginar). El orden lo
  // aplica el servidor: con paginación, ordenar solo la página visible daría un orden engañoso.
  const filterParams = useMemo(() => {
    const params = new URLSearchParams({ sort: sort.key, dir: sort.dir });
    if (nombre) params.set("nombre", nombre);
    if (estado) params.set("estado", estado);
    if (isSuperAdmin && providerId) params.set("providerId", providerId);
    return params;
  }, [nombre, estado, providerId, isSuperAdmin, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ["championships", page, limit, filterParams.toString()],
    queryFn: async () => {
      const params = new URLSearchParams(filterParams);
      params.set("page", String(page));
      params.set("limit", String(limit));
      return (await api.get<PaginatedResult<ChampionshipListItem>>(`/championships?${params.toString()}`)).data;
    },
    placeholderData: keepPreviousData,
  });

  const rows = data?.data ?? [];

  function toggleSort(key: SortKey) {
    setSort((current) => nextSort(current, key));
    setPage(1);
  }

  const hasFilters = Boolean(nombre || estado || providerId);

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumbs items={[{ label: "Campeonatos" }]} />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
            <Trophy className="h-6 w-6" />
          </span>
          <div>
            <h1 className="text-2xl font-bold text-ink-900">Campeonatos</h1>
            <p className="text-ink-400">Torneos con parejas inscritas y partidos programados en pista. Pincha en uno para ver su ficha.</p>
          </div>
        </div>
        {canCreate && (
          <Button onClick={() => navigate("/campeonatos/nuevo")}>
            <Plus className="h-4 w-4" />
            Nuevo campeonato
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Input label="Nombre" placeholder="Buscar campeonato…" value={nombreInput} onChange={(e) => {
              setNombreInput(e.target.value);
              setPage(1);
            }}
            className="w-full"
          />
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-ink-600">Estado</label>
            <select
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value as "" | ChampionshipStatus);
                setPage(1);
              }}
              className={selectClass}
            >
              <option value="">Todos</option>
              {(Object.keys(CHAMPIONSHIP_STATUS_LABEL) as ChampionshipStatus[]).map((s) => (
                <option key={s} value={s}>
                  {CHAMPIONSHIP_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>
          {isSuperAdmin && (
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-ink-600">Organizador</label>
              <select
                value={providerId}
                onChange={(e) => {
                  setProviderId(e.target.value);
                  setPage(1);
                }}
                className={selectClass}
              >
                <option value="">Todos</option>
                {providers?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {personName(p)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {hasFilters ? (
            <Button
              onClick={() => {
                setNombreInput("");
                setEstado("");
                setProviderId("");
                setPage(1);
              }}
            >
              Limpiar filtros
            </Button>
          ) : (
            <span />
          )}
          <ExportExcelButton url={`/championships/export.xlsx?${filterParams.toString()}`} filename="campeonatos.xlsx" />
        </div>
      </div>

      <Card className="overflow-hidden p-0">
        {isLoading ? (
          <TableSkeleton rows={3} cols={6} />
        ) : !rows.length ? (
          <p className="p-6 text-sm text-ink-400">
            {hasFilters ? "Ningún campeonato coincide con los filtros." : "Todavía no hay ningún campeonato."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed text-left text-sm">
              <thead className="bg-ink-50/60 text-xs tracking-wide text-ink-400 uppercase">
                <tr>
                  <th className="px-4 py-3 sm:px-6">
                    <SortHeader label="Campeonato" active={sort.key === "nombre"} dir={sort.dir} onClick={() => toggleSort("nombre")} />
                  </th>
                  <th className="hidden px-4 py-3 md:table-cell md:w-[22%] lg:px-6">
                    <SortHeader label="Organizador" active={sort.key === "organizador"} dir={sort.dir} onClick={() => toggleSort("organizador")} />
                  </th>
                  <th className="hidden px-4 py-3 sm:table-cell sm:w-28 lg:px-6">
                    <SortHeader label="Parejas" active={sort.key === "equiposCount"} dir={sort.dir} onClick={() => toggleSort("equiposCount")} />
                  </th>
                  <th className="hidden px-4 py-3 sm:table-cell sm:w-28 lg:px-6">
                    <SortHeader label="Partidos" active={sort.key === "partidosCount"} dir={sort.dir} onClick={() => toggleSort("partidosCount")} />
                  </th>
                  <th className="w-32 px-4 py-3 sm:px-6">
                    <SortHeader label="Estado" active={sort.key === "estado"} dir={sort.dir} onClick={() => toggleSort("estado")} />
                  </th>
                  <th className="hidden px-6 py-3 xl:table-cell xl:w-48" />
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/campeonatos/${c.id}`)}
                    className="cursor-pointer border-t border-ink-100 transition-colors hover:bg-ink-50/50"
                  >
                    <td className="px-4 py-3.5 sm:px-6">
                      <p className="truncate font-medium text-ink-800" title={c.nombre}>
                        {c.nombre}
                      </p>
                      {/* Lo que no cabe en columnas en pantallas estrechas va bajo el nombre */}
                      <p className="truncate text-xs text-ink-400 md:hidden">
                        {c.provider ? personName(c.provider) : "Plataforma"}
                        <span className="sm:hidden">
                          {" "}
                          · {c.equiposCount} parejas · {c.partidosCount} partidos
                        </span>
                      </p>
                    </td>
                    <td className="hidden truncate px-4 py-3.5 text-ink-500 md:table-cell lg:px-6">{c.provider ? personName(c.provider) : "Plataforma"}</td>
                    <td className="hidden px-4 py-3.5 text-ink-500 sm:table-cell lg:px-6">{c.equiposCount}</td>
                    <td className="hidden px-4 py-3.5 text-ink-500 sm:table-cell lg:px-6">{c.partidosCount}</td>
                    <td className="px-4 py-3.5 sm:px-6">
                      <Badge tone={championshipTone(c.estado)}>{CHAMPIONSHIP_STATUS_LABEL[c.estado]}</Badge>
                    </td>
                    <td className="hidden px-6 py-3.5 text-right xl:table-cell">
                      {c.canManage ? <Badge tone="green">Puedes gestionarlo</Badge> : <Badge tone="slate">Solo lectura</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Pagination
        page={data?.page ?? 1}
        totalPages={data?.totalPages ?? 1}
        total={data?.total ?? 0}
        onPageChange={setPage}
        limit={limit}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
      />
    </div>
  );
}
